import { ChangeEvent } from 'react';
import { CartItem, ProductWithUI } from '../types';
import { SearchBar } from '../shared/components';
import { NotificationList } from './components/notification';
import { useCart, useSearch, useProduct, useNotification } from './hooks';
import { useToggle } from '../shared/hooks/useToggle';
import { AdminPage } from './pages/AdminPage';
import { CartPage } from './pages/CartPage';

export default function App() {
  const { notifications, addNotification, removeNotification } = useNotification();
  const { products } = useProduct();
  const { toggle: toggleAdmin, isOpen: isAdmin } = useToggle(false);
  const { searchTerm, handleSearchTermChange, debouncedSearchTerm, filteredProducts } =
    useSearch(products);

  return (
    <div className="min-h-screen bg-gray-50">
      <NotificationList notifications={notifications} onDismiss={removeNotification} />

      <Header
        isAdmin={isAdmin}
        toggleAdmin={toggleAdmin}
        searchTerm={searchTerm}
        onSearchTermChange={handleSearchTermChange}
      />

      <MainContent
        isAdmin={isAdmin}
        filteredProducts={filteredProducts}
        debouncedSearchTerm={debouncedSearchTerm}
        addNotification={addNotification}
      />
    </div>
  );
}

interface HeaderProps {
  isAdmin: boolean;
  toggleAdmin: () => void;
  searchTerm: string;
  onSearchTermChange: (e: ChangeEvent<HTMLInputElement>) => void;
}

function Header({ isAdmin, toggleAdmin, searchTerm, onSearchTermChange }: HeaderProps) {
  const { cart, totalItemCount } = useCart();

  return (
    <header className="bg-white shadow-sm sticky top-0 z-40 border-b">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center flex-1 gap-6">
            <h1 className="text-xl font-semibold text-gray-800">SHOP</h1>
            {!isAdmin && <SearchBar value={searchTerm} onChange={onSearchTermChange} />}
          </div>
          <nav className="flex items-center space-x-4">
            <AdminToggleButton isAdmin={isAdmin} onToggle={toggleAdmin} />
            {!isAdmin && <CartIcon cart={cart} totalItemCount={totalItemCount} />}
          </nav>
        </div>
      </div>
    </header>
  );
}

interface AdminToggleButtonProps {
  isAdmin: boolean;
  onToggle: () => void;
}

function AdminToggleButton({ isAdmin, onToggle }: AdminToggleButtonProps) {
  return (
    <button
      onClick={onToggle}
      className={`px-3 py-1.5 text-sm rounded transition-colors ${
        isAdmin ? 'bg-gray-800 text-white' : 'text-gray-600 hover:text-gray-900'
      }`}
    >
      {isAdmin ? '쇼핑몰로 돌아가기' : '관리자 페이지로'}
    </button>
  );
}

interface CartIconProps {
  cart: CartItem[];
  totalItemCount: number;
}

function CartIcon({ cart, totalItemCount }: CartIconProps) {
  return (
    <div className="relative">
      <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
        />
      </svg>
      {cart.length > 0 && (
        <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
          {totalItemCount}
        </span>
      )}
    </div>
  );
}

interface MainContentProps {
  isAdmin: boolean;
  filteredProducts: ProductWithUI[];
  debouncedSearchTerm: string;
  addNotification: (message: string, type?: 'error' | 'success' | 'warning') => void;
}

function MainContent({
  isAdmin,
  filteredProducts,
  debouncedSearchTerm,
  addNotification,
}: MainContentProps) {
  return (
    <main className="max-w-7xl mx-auto px-4 py-8">
      {isAdmin ? (
        <AdminPage addNotification={addNotification} />
      ) : (
        <CartPage
          filteredProducts={filteredProducts}
          debouncedSearchTerm={debouncedSearchTerm}
          addNotification={addNotification}
        />
      )}
    </main>
  );
}
